import { useEffect, useRef, useState } from "react";

const RELEASE_COMMIT_PATTERN = /^[0-9a-f]{40}$/;
const RELEASE_DIGEST_PATTERN = /^[0-9a-f]{64}$/;
const PUBLISHED_ENVIRONMENTS = new Set(["stg", "prod"]);

const injectedRelease = () => (
  typeof __AMPLI_RELEASE__ === "undefined"
  || typeof __AMPLI_RELEASE_MARKER__ === "undefined"
    ? null
    : { ...__AMPLI_RELEASE__, marker: __AMPLI_RELEASE_MARKER__ }
);

const isLoadedRelease = (release) => (
  release
  && PUBLISHED_ENVIRONMENTS.has(release.environment)
  && RELEASE_COMMIT_PATTERN.test(release.commit)
  && release.marker === `ampli-release-v1:${release.environment}:${release.commit}`
);

const isPublishedRelease = (release, environment) => (
  release
  && release.schemaVersion === 2
  && release.environment === environment
  && RELEASE_COMMIT_PATTERN.test(release.commit)
  && release.client?.file === `client-prod-${release.commit}.js`
  && RELEASE_DIGEST_PATTERN.test(release.client?.sha256)
  && Number.isSafeInteger(release.client?.bytes)
  && release.client.bytes > 0
  && release.css?.file === `assets/css/main-${release.commit}.css`
  && RELEASE_DIGEST_PATTERN.test(release.css?.sha256)
  && Number.isSafeInteger(release.css?.bytes)
  && release.css.bytes > 0
);

const defaultFetchRelease = (...args) => window.fetch(...args);
const defaultReloadPage = () => window.location.reload();

const ReleaseUpdateNotice = ({
  loadedRelease = injectedRelease(),
  fetchRelease = defaultFetchRelease,
  reloadPage = defaultReloadPage,
  pollIntervalMs = 60000,
}) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const requestRevision = useRef(0);

  useEffect(() => {
    if (!isLoadedRelease(loadedRelease)) return undefined;

    let active = true;

    const checkPublishedRelease = async () => {
      const revision = requestRevision.current + 1;
      requestRevision.current = revision;

      try {
        const response = await fetchRelease(
          `/release.json?loaded=${loadedRelease.commit}&t=${Date.now()}`,
          {
            cache: "no-store",
            headers: {
              Accept: "application/json",
              "Cache-Control": "no-cache",
            },
          },
        );
        if (!active || revision !== requestRevision.current || !response?.ok) return;

        const publishedRelease = await response.json();
        if (!active || revision !== requestRevision.current) return;
        if (isPublishedRelease(publishedRelease, loadedRelease.environment)) {
          setUpdateAvailable(publishedRelease.commit !== loadedRelease.commit);
        }
      } catch (_) {
        // A consulta é apenas informativa. Falhas de rede nunca interrompem o trabalho.
      }
    };

    checkPublishedRelease();

    const intervalId = pollIntervalMs > 0
      ? window.setInterval(checkPublishedRelease, pollIntervalMs)
      : null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkPublishedRelease();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      active = false;
      requestRevision.current += 1;
      if (intervalId !== null) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchRelease, loadedRelease?.commit, loadedRelease?.environment, pollIntervalMs]);

  if (!updateAvailable) return null;

  return (
    <aside className="release-update-notice" role="status" aria-live="polite">
      <span>Uma nova versão do Ampli está disponível. Salve seu trabalho antes de atualizar.</span>
      <button type="button" onClick={reloadPage}>Recarregar agora</button>
    </aside>
  );
};

export default ReleaseUpdateNotice;
